import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, SlidersHorizontal, Grid3X3, LayoutGrid, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: '', label: 'Todos', emoji: '' },
  { key: 'limpiadores', label: 'Limpiadores', emoji: '' },
  { key: 'toners', label: 'Toners', emoji: '' },
  { key: 'serums', label: 'Serums', emoji: '' },
  { key: 'protectores_solares', label: 'Protectores Solares', emoji: '' },
  { key: 'piel_grasa', label: 'Piel Grasa', emoji: '' },
  { key: 'piel_sensible', label: 'Piel Sensible', emoji: '' },
  { key: 'blotting_papers', label: 'Blotting Papers', emoji: '' },
  { key: 'kits', label: 'Kits', emoji: '' },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gridCols, setGridCols] = useState(4);
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (search) params.search = search;
    axios.get(`${API}/products`, { params }).then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [activeCategory, search]);

  return (
    <div className="min-h-screen bg-white" data-testid="shop-page">
      {/* Header */}
      <div className="bg-[var(--cream)] border-b border-[var(--sage)]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--dark)] mb-1">Tienda K-Beauty</h1>
          <p className="text-sm text-[var(--dark)]/40">Skincare coreano autentico seleccionado para tu piel</p>
        </div>
      </div>

      {/* Category Navigation - Olive Young style */}
      <div className="border-b border-[var(--sage)]/10 bg-white sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide -mx-1">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setSearchParams(cat.key ? { category: cat.key } : {})}
                className={`category-tag whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold transition-all flex-shrink-0 ${
                  activeCategory === cat.key
                    ? 'bg-[var(--violet)] text-white active'
                    : 'text-[var(--dark)]/50 hover:text-[var(--dark)] hover:bg-[var(--cream)]'
                }`}
                data-testid={`filter-${cat.key || 'all'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dark)]/25" />
              <Input
                placeholder="Buscar productos..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 rounded-lg border-[var(--sage)]/30 bg-[var(--cream)]/50 h-9 text-sm focus:bg-white"
                data-testid="search-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="h-3 w-3 text-[var(--dark)]/30" />
                </button>
              )}
            </div>
            {activeCategory && (
              <Badge className="bg-[var(--violet-light)] text-[var(--violet)] border-0 text-xs cursor-pointer hover:bg-[var(--violet)] hover:text-white transition-colors" onClick={() => setSearchParams({})}>
                {categories.find(c => c.key === activeCategory)?.label} <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
          </div>
          <div className="hidden lg:flex items-center gap-2 text-[var(--dark)]/30">
            <span className="text-xs">{products.length} productos</span>
            <div className="flex gap-1 ml-2">
              <button onClick={() => setGridCols(3)} className={`p-1.5 rounded ${gridCols === 3 ? 'bg-[var(--cream)] text-[var(--dark)]' : ''}`}>
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button onClick={() => setGridCols(4)} className={`p-1.5 rounded ${gridCols === 4 ? 'bg-[var(--cream)] text-[var(--dark)]' : ''}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className={`grid grid-cols-2 ${gridCols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4 lg:gap-5`}>
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse border border-[var(--sage)]/10">
                <div className="aspect-[4/5] bg-[var(--cream)]"></div>
                <div className="p-4 space-y-2.5"><div className="h-2.5 bg-[var(--cream)] rounded w-1/3"></div><div className="h-3 bg-[var(--cream)] rounded w-3/4"></div><div className="h-4 bg-[var(--cream)] rounded w-1/4 mt-2"></div></div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className={`grid grid-cols-2 ${gridCols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4 lg:gap-5`} data-testid="products-grid">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-base text-[var(--dark)]/30">No se encontraron productos</p>
            <Button variant="ghost" className="mt-4 text-[var(--violet)]" onClick={() => { setSearch(''); setSearchParams({}); }}>Ver todos los productos</Button>
          </div>
        )}
      </div>
    </div>
  );
}
